import React, { useState, useEffect } from 'react';
import { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { findCodePrefix, nextSequence } from '../../utils/codePrefixUtils';

interface FOACodePrefixFieldProps {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
  codePrefixes: any[];
  isEditing: boolean;
  initialFoaNumber?: string;
}

const FOACodePrefixField: React.FC<FOACodePrefixFieldProps> = ({
  register,
  setValue,
  errors,
  codePrefixes,
  isEditing,
  initialFoaNumber,
}) => {
  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [foaSequenceNumber, setFoaSequenceNumber] = useState<string>('001');

  useEffect(() => {
    if (initialFoaNumber && codePrefixes.length > 0) {
      const matchingPrefix = codePrefixes.find((p: any) =>
        initialFoaNumber.startsWith(p.prefix)
      );
      if (matchingPrefix) {
        setSelectedCodePrefix(String(matchingPrefix.id));
        setFoaSequenceNumber(initialFoaNumber.replace(matchingPrefix.prefix, ''));
      }
    }
  }, [initialFoaNumber, codePrefixes]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        FOA Number <span className="text-red-500">*</span>
      </label>
      <div className="flex gap-2">
        <select
          value={selectedCodePrefix}
          onChange={(e) => {
            setSelectedCodePrefix(e.target.value);
            const prefix = findCodePrefix(codePrefixes, e.target.value);
            if (prefix) {
              const nextNum = String(nextSequence(prefix)).padStart(3, '0');
              setFoaSequenceNumber(nextNum);
              setValue('foaNumber', `${prefix.prefix}${nextNum}`, { shouldValidate: true });
            } else {
              setValue('foaNumber', '', { shouldValidate: false });
            }
          }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          disabled={isEditing}
        >
          <option value="">Select Code Prefix</option>
          {codePrefixes.map((prefix: any) => (
            <option key={prefix.id} value={prefix.id}>
              {prefix.prefix} - {prefix.name || prefix.description || ''}
            </option>
          ))}
        </select>
      </div>
      {selectedCodePrefix && (
        <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
          <span className="text-sm text-gray-600">Generated Code: </span>
          <span className="text-sm font-semibold text-primary-600">
            {findCodePrefix(codePrefixes, selectedCodePrefix)?.prefix}{foaSequenceNumber}
          </span>
        </div>
      )}
      <input type="hidden" {...register('foaNumber', { required: 'FOA number is required' })} />
      {errors.foaNumber?.message && (
        <p className="mt-1 text-sm text-red-600">{errors.foaNumber.message as string}</p>
      )}
    </div>
  );
};

export default FOACodePrefixField;
